<template>
    <v-hover v-slot="{ hover }">
        <v-card
            class="d-flex flex-column w-100 fill-height transition-swing pointer"
            :class="isInactive ? 'text--disabled' : ''"
            :elevation="hover ? 10 : 2"
            :style="{ transform: hover ? 'scale(1.02)' : 'scale(1)' }">
            <div @click="openSpace()">
                <v-list three-line color="transparent" class="py-0">
                    <v-list-item>
                        <v-list-item-avatar>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn fab x-small :color="typeButtonColor" elevation="0" v-bind="attrs" v-on="on">
                                        <v-icon small :class="typeIconColorClass">{{ typeIcon }}</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ typeLabel }}</span>
                            </v-tooltip>
                        </v-list-item-avatar>

                        <v-list-item-content>
                            <v-list-item-title>
                                <b>{{ space.space_long_id }}</b>
                            </v-list-item-title>
                            <v-list-item-subtitle>{{ space.space_description }}</v-list-item-subtitle>
                        </v-list-item-content>

                        <v-list-item-action>
                            <div class="d-flex align-center">
                                <v-menu v-model="actionsMenu" offset-y left>
                                    <template v-slot:activator="{ on }">
                                        <v-btn text color="secondary" small v-on="on" @click.stop>
                                            <v-icon>mdi-dots-horizontal</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense nav>
                                        <v-list-item @click.stop="gotoEditSpace()">
                                            <v-list-item-icon>
                                                <v-icon>mdi-pencil-outline</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="mr-4">Edit</v-list-item-title>
                                        </v-list-item>
                                        <SpaceInviteDialog
                                            :space-id="space.sid"
                                            :space-type="spaceType"
                                            :space-archived="Boolean(space.space_archival_timestamp)"
                                            @close="actionsMenu = false">
                                            <template v-slot:activator="{ attrs, open }">
                                                <v-list-item v-bind="attrs" :disabled="!canManageUsers" @click.stop="open">
                                                    <v-list-item-icon>
                                                        <v-icon>mdi-account-plus-outline</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-title class="mr-4">Invite users</v-list-item-title>
                                                </v-list-item>
                                            </template>
                                        </SpaceInviteDialog>
                                        <v-list-item @click.stop="toggleFavorite()">
                                            <v-list-item-icon>
                                                <v-icon>{{ favoriteActionIcon }}</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="mr-4">{{ favoriteActionLabel }}</v-list-item-title>
                                        </v-list-item>
                                        <v-divider class="my-2"></v-divider>
                                        <EntityDeleteDialog
                                            entity-type="space"
                                            :organization-id="$route.params.oid"
                                            :space-id="space.sid"
                                            :space-name="space.space_long_id">
                                            <template v-slot:activator="{ attrs, open }">
                                                <v-list-item v-bind="attrs" :disabled="!canManageUsers" @click.stop="open">
                                                    <v-list-item-icon>
                                                        <v-icon color="error">mdi-trash-can-outline</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-title class="mr-4 error--text">Delete</v-list-item-title>
                                                </v-list-item>
                                            </template>
                                        </EntityDeleteDialog>
                                    </v-list>
                                </v-menu>
                            </div>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
            </div>
        </v-card>
    </v-hover>
</template>

<script>
import { mapState } from 'vuex'
import { isAdminRole } from '@/utils'
import { enumsData } from '@/mixins/enums'
import spaceTypeUi from '@/mixins/spaceTypeUi'
import EntityDeleteDialog from '@/components/EntityDeleteDialog.vue'
import SpaceInviteDialog from '@/modules/space/components/SpaceInviteDialog.vue'

export default {
    name: 'SpaceCard',
    mixins: [enumsData, spaceTypeUi],
    components: { EntityDeleteDialog, SpaceInviteDialog },
    props: {
        space: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            actionsMenu: false
        }
    },
    computed: {
        ...mapState(['userMetadata']),
        ...mapState('orgStore', ['educationSpaces', 'researchSpaces', 'datasetSpaces']),
        isInactive() {
            return !!(this.space.space_archival_timestamp || this.space.space_rested_timestamp)
        },
        typeButtonColor() {
            return this.spaceTypeButtonColor(this.space)
        },
        typeIconColorClass() {
            return this.spaceTypeIconColorClass(this.space)
        },
        typeIcon() {
            return this.spaceTypeIcon(this.space)
        },
        typeLabel() {
            return this.spaceTypeLabel(this.space)
        },
        spaceType() {
            const enumKey = this.spaceTypeMetaByKey(this.getSpaceTypeKey(this.space)).spaceTypeEnumKey
            return enumKey ? this.spaceTypes[enumKey] : null
        },
        canManageUsers() {
            return isAdminRole(this.space.space_role)
        },
        favoriteSpaceSids() {
            if (!Array.isArray(this.userMetadata?.favorite_space_sids)) {
                return []
            }
            return this.userMetadata.favorite_space_sids.map(sid => sid.toString())
        },
        isFavoriteState() {
            const sid = this.space?.sid?.toString()
            return !!sid && this.favoriteSpaceSids.includes(sid)
        },
        favoriteActionLabel() {
            return this.isFavoriteState ? 'Remove from favourites' : 'Add to favourites'
        },
        favoriteActionIcon() {
            return this.isFavoriteState ? 'mdi-star-off-outline' : 'mdi-star-outline'
        }
    },
    methods: {
        toggleFavorite() {
            const sid = this.space?.sid?.toString()
            if (!sid) return

            const currentFavorites = this.favoriteSpaceSids
            const updatedFavorites = currentFavorites.includes(sid)
                ? currentFavorites.filter(currentSid => currentSid !== sid)
                : [sid, ...currentFavorites.filter(currentSid => currentSid !== sid)]

            this.$store.commit('setUserMetadata', { favorite_space_sids: updatedFavorites })
        },
        openSpace() {
            this.$router.push({
                name: 'snapshot-overview',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.space.sid,
                    iid: this.space.iid,
                    snid: this.space.snid
                }
            })
        },
        gotoEditSpace() {
            this.$router.push({
                name: 'config-space-general',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.space.sid
                }
            })
        }
    }
}
</script>
